import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FaTruck, FaPlane, FaTrain, FaShip } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from 'emailjs-com';

const QuoteForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm();
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91'); // Default to India
  
  // Create refs for location inputs
  const pickupRef = useRef(null);
  const deliveryRef = useRef(null);

  // Country codes with flags
  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+7', country: 'Russia', flag: '🇷🇺' },
  ];

  const transportModes = [
    { id: 'road', name: 'Road', icon: <FaTruck /> },
    { id: 'air', name: 'Air', icon: <FaPlane /> },
    { id: 'train', name: 'Train', icon: <FaTrain /> },
    { id: 'ship', name: 'Ship', icon: <FaShip /> }
  ];

  const cargoTypes = [
    { id: 'general', name: 'General Cargo' },
    { id: 'fragile', name: 'Fragile Items' },
    { id: 'hazardous', name: 'Hazardous Materials' },
    { id: 'temperature', name: 'Temperature-Sensitive' },
    { id: 'oversized', name: 'Oversized Cargo' },
    { id: 'liquid', name: 'Liquid Cargo' }
  ];

  const urgencyLevels = [
    { id: 'standard', name: 'Standard Delivery' },
    { id: 'express', name: 'Express Delivery' },
    { id: 'same-day', name: 'Same-Day Delivery' }
  ];

  // Initialize Google Maps autocomplete - moved inside the component
  useEffect(() => {
    // Load Google Maps API script dynamically
    const loadGoogleMapsScript = () => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeAutocomplete;
      document.head.appendChild(script);
    };

    // Initialize autocomplete functionality
    const initializeAutocomplete = () => {
      if (pickupRef.current && deliveryRef.current) {
        const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupRef.current, {
          types: ['(cities)']
        });
        
        const deliveryAutocomplete = new window.google.maps.places.Autocomplete(deliveryRef.current, {
          types: ['(cities)']
        });
        
        pickupAutocomplete.addListener('place_changed', () => {
          const place = pickupAutocomplete.getPlace();
          if (place.formatted_address) {
            setValue('pickupLocation', place.formatted_address);
          }
        });
        
        deliveryAutocomplete.addListener('place_changed', () => {
          const place = deliveryAutocomplete.getPlace();
          if (place.formatted_address) {
            setValue('deliveryLocation', place.formatted_address);
          }
        });
      }
    };

    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps) {
      initializeAutocomplete();
    } else {
      loadGoogleMapsScript();
    }

    // Cleanup function
    return () => {
      // Remove any event listeners if needed
    };
  }, [setValue]);
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Prepare data for email
      const emailData = {
        name: data.name,
        email: data.email,
        phone: `${selectedCountryCode} ${data.phone}`,
        company: data.company || 'Not provided',
        pickupLocation: data.pickupLocation,
        deliveryLocation: data.deliveryLocation,
        transportMode: data.transportMode,
        cargoType: data.cargoType || 'Not specified',
        weight: data.weight || 'Not specified',
        urgency: data.urgency || 'Standard',
        additionalInfo: data.additionalInfo || 'None provided',
        subject: `New Quote Request from ${data.name}`
      };
  
      console.log('Quote request data:', data);
      
      // Send email using EmailJS with public key
      await emailjs.send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_TEMPLATE_ID,
        emailData,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY
      );
      
      setSubmitSuccess(true);
      reset();
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rest of the component remains the same
  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-6 md:p-8">
      {submitSuccess ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="text-green-500 text-5xl mb-4">✓</div>
          <h3 className="text-2xl font-bold mb-2 text-secondary-900 dark:text-white">Quote Request Submitted!</h3>
          <p className="text-secondary-600 dark:text-secondary-300 mb-6">
            Thank you for your request. Our team will contact you shortly with a detailed quote.
          </p>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-300"
          >
            Submit Another Request
          </button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <h2 className="text-2xl font-bold mb-6 text-secondary-900 dark:text-white">Request a Shipping Quote</h2>
          
          {/* Contact Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-secondary-900 dark:text-white">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="name">
                  Full Name *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full px-4 py-2 rounded-md border ${errors.name ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  {...register('name', { required: 'Name is required' })}
                />
                {errors.name && <p className="mt-1 text-red-500 text-sm">{errors.name.message}</p>}
              </div>
              
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="email">
                  Email Address *
                </label>
                <input
                  id="email"
                  type="email"
                  className={`w-full px-4 py-2 rounded-md border ${errors.email ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && <p className="mt-1 text-red-500 text-sm">{errors.email.message}</p>}
              </div>
              
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="phone">
                  Phone Number *
                </label>
                <div className="flex">
                  <div className="relative w-1/3 mr-2">
                    <select
                      className="w-full px-4 py-2 rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none pr-8"
                      value={selectedCountryCode}
                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                      {...register('countryCode')}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-4 h-4 fill-current text-secondary-500" viewBox="0 0 20 20">
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </div>
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    className={`w-2/3 px-4 py-2 rounded-md border ${errors.phone ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    onKeyPress={(e) => {
                      // Allow only numbers
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    {...register('phone', { 
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Please enter only numbers'
                      }
                    })}
                  />
                </div>
                {errors.phone && <p className="mt-1 text-red-500 text-sm">{errors.phone.message}</p>}
              </div>
              
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="company">
                  Company Name
                </label>
                <input
                  id="company"
                  type="text"
                  className="w-full px-4 py-2 rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  {...register('company')}
                />
              </div>
            </div>
          </div>
          
          {/* Shipment Details */}
          <div className="mb-8">
          </div>
            <h3 className="text-lg font-semibold mb-4 text-secondary-900 dark:text-white">Shipment Details</h3>
            
            {/* Pickup and Delivery Locations with Google Maps Autocomplete */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="pickupLocation">
                  Pickup Location *
                </label>
                <input
                  id="pickupLocation"
                  ref={pickupRef}
                  type="text"
                  className={`w-full px-4 py-2 rounded-md border ${errors.pickupLocation ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Start typing city name..."
                  {...register('pickupLocation', { required: 'Pickup location is required' })}
                />
                {errors.pickupLocation && <p className="mt-1 text-red-500 text-sm">{errors.pickupLocation.message}</p>}
              </div>
              
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="deliveryLocation">
                  Delivery Location *
                </label>
                <input
                  id="deliveryLocation"
                  ref={deliveryRef}
                  type="text"
                  className={`w-full px-4 py-2 rounded-md border ${errors.deliveryLocation ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder="Start typing city name..."
                  {...register('deliveryLocation', { required: 'Delivery location is required' })}
                />
                {errors.deliveryLocation && <p className="mt-1 text-red-500 text-sm">{errors.deliveryLocation.message}</p>}
              </div>
            </div>
            
            {/* Transport Mode */}
            <div className="mb-4">
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2">
                Transport Mode *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {transportModes.map((mode) => (
                  <label 
                    key={mode.id}
                    className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                      errors.transportMode ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'
                    } hover:bg-secondary-50 dark:hover:bg-secondary-700 ${
                      watch('transportMode') === mode.id ? 'bg-primary-50 border-primary-500 dark:bg-primary-900 dark:border-primary-400' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      value={mode.id}
                      className="sr-only" // Changed from "hidden" to "sr-only"
                      {...register('transportMode', { required: 'Please select a transport mode' })}
                    />
                    <div className={`flex flex-col items-center w-full ${
                      errors.transportMode ? 'text-red-500' : 
                      watch('transportMode') === mode.id ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-900 dark:text-white'
                    }`}>
                      <div className="text-2xl mb-1">{mode.icon}</div>
                      <span className="text-sm font-medium">{mode.name}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.transportMode && <p className="mt-1 text-red-500 text-sm">{errors.transportMode.message}</p>}
            </div>
            
            {/* Cargo Type */}
            <div className="mb-4">
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2">
                Cargo Type *
              </label>
              <select
                className={`w-full px-4 py-2 rounded-md border ${
                  errors.cargoType ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'
                } bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500`}
                {...register('cargoType', { required: 'Please select a cargo type' })}
              >
                <option value="">Select cargo type</option>
                {cargoTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.cargoType && <p className="mt-1 text-red-500 text-sm">{errors.cargoType.message}</p>}
            </div>
            
            {/* Weight field only - dimensions removed */}
            <div className="mb-4">
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="weight">
                  Weight (kg)
                </label>
                <input
                  id="weight"
                  type="number"
                  min="0"
                  step="0.1"
                  className="w-full px-4 py-2 rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  {...register('weight')}
                />
              </div>
            </div>
            
            {/* Urgency Level */}
            <div className="mb-4">
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2">
                Urgency Level
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {urgencyLevels.map((level) => (
                  <label 
                    key={level.id}
                    className="flex items-center p-3 border border-secondary-300 dark:border-secondary-600 rounded-md cursor-pointer transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-700"
                  >
                    <input
                      type="radio"
                      value={level.name}
                      className="mr-2"
                      {...register('urgency')}
                    />
                    <span className="text-secondary-900 dark:text-white">{level.name}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Additional Information */}
            <div className="mb-4">
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2" htmlFor="additionalInfo">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                rows="4"
                className="w-full px-4 py-2 rounded-md border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Any special requirements or information about your shipment..."
                {...register('additionalInfo')}
              ></textarea>
            </div>
            
            {/* Submit button */}
            <div className="text-center mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Submitting...' : 'Request Quote'}
              </button>
            </div>
          </form>
      )}
    </div>
  );
};

export default QuoteForm;